import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Braces, Building2, Check, ClipboardCheck, Cpu, Factory,
  Dumbbell, GraduationCap, HeartPulse, Languages, Map, MapPin, PackageSearch, Search, ShieldCheck,
  Ship, ShoppingBag, Sparkles, Truck, Users, Zap,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { img } from "@/data/images";
import { HOW_IT_WORKS } from "@/data/network";
import { useSlot } from "@/context/ImageSlotsContext";
import { SeoHead } from "@/components/SeoHead";
import { Reveal } from "@/components/Reveal";

const RESPONSIVE_SIZES = "(max-width: 768px) 100vw, (max-width: 1280px) 70vw, 900px";

function SmartImage({ src, alt, className = "", eager = false, sizes = RESPONSIVE_SIZES }) {
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={eager ? "high" : "auto"}
      sizes={sizes}
    />
  );
}

function Hero() {
  const { lc } = useI18n();
  const slot = useSlot("home.hero");
  const heroSrc = slot?.src || img("stp_hero_coast");

  useEffect(() => {
    if (!heroSrc) return undefined;
    const existing = document.querySelector(`link[data-bsgo-hero="${heroSrc}"]`);
    if (existing) return undefined;
    const preload = document.createElement("link");
    preload.rel = "preload";
    preload.as = "image";
    preload.href = heroSrc;
    preload.fetchPriority = "high";
    preload.dataset.bsgoHero = heroSrc;
    document.head.appendChild(preload);
    return () => preload.remove();
  }, [heroSrc]);

  const audiences = [
    { icon: Users, pt: "Pessoas", en: "People" },
    { icon: Building2, pt: "Negócios", en: "Businesses" },
    { icon: MapPin, pt: "Comunidades", en: "Communities" },
    { icon: ShieldCheck, pt: "Instituições", en: "Institutions" },
  ];

  return (
    <section className="home-hero relative isolate min-h-[680px] overflow-hidden bg-navy text-white" data-testid="home-hero">
      <SmartImage src={heroSrc} alt={slot?.alt || lc({ pt: "Vista aérea real da costa de São Tomé e Príncipe", en: "Real aerial view of the São Tomé and Príncipe coastline" })} eager className="home-hero-media absolute inset-0 h-full w-full object-cover object-[58%_center] sm:object-center" sizes="100vw" />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(3,15,28,.93)_0%,rgba(5,29,49,.78)_38%,rgba(4,26,44,.34)_70%,rgba(3,15,28,.12)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(0deg,rgba(3,15,28,.62)_0%,transparent_38%)]" />
      <div className="relative mx-auto flex min-h-[680px] max-w-[1500px] flex-col justify-between px-6 pb-8 pt-24 sm:px-8 lg:px-12 lg:pb-10 lg:pt-32">
        <div className="max-w-[760px]">
          <div className="premium-overline text-[#f4c15d]">{lc({ pt: "SÃO TOMÉ E PRÍNCIPE · ÁFRICA · CAPACIDADE GLOBAL", en: "SÃO TOMÉ AND PRÍNCIPE · AFRICA · GLOBAL CAPABILITY" })}</div>
          <h1 className="mt-5 max-w-[720px] font-display text-[clamp(3.3rem,7vw,7.2rem)] font-black leading-[.88] tracking-[-.055em]">
            {lc({ pt: "Da necessidade", en: "From need" })}<br />
            <span className="text-[#f4c15d]">{lc({ pt: "à solução.", en: "to solution." })}</span>
          </h1>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-white/82 sm:text-lg lg:text-xl">
            {lc({
              pt: "Coordenamos produtos, equipamentos, fornecedores, especialistas e capacidades para pessoas, negócios, comunidades e instituições.",
              en: "We coordinate products, equipment, suppliers, specialists and capabilities for people, businesses, communities and institutions.",
            })}
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Link to="/nexis-quiz" data-testid="hero-primary-cta" className="premium-button premium-button-gold">
              {lc({ pt: "Diga-nos o que precisa", en: "Tell us what you need" })}<ArrowRight size={17} />
            </Link>
            <Link to="/solutions" data-testid="hero-secondary-cta" className="premium-button premium-button-ghost">
              {lc({ pt: "Explorar soluções", en: "Explore solutions" })}<ArrowRight size={17} />
            </Link>
          </div>
        </div>

        <div className="mt-12 grid max-w-3xl grid-cols-2 border-t border-white/20 sm:grid-cols-4">
          {audiences.map(({ icon: Icon, pt, en }) => (
            <div key={en} className="flex items-center gap-2.5 border-white/20 py-4 pr-4 text-xs font-medium text-white/78 sm:border-r sm:px-4 sm:first:pl-0 sm:last:border-r-0">
              <Icon size={17} className="text-[#f4c15d]" />{lc({ pt, en })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SolutionSearch() {
  const { lc } = useI18n();
  return (
    <section className="border-b border-slate-200 bg-white" data-testid="home-solution-search">
      <div className="mx-auto grid max-w-[1500px] gap-5 px-6 py-8 sm:px-8 lg:grid-cols-[1fr_minmax(420px,680px)] lg:items-center lg:px-12">
        <div>
          <div className="premium-overline text-bsgo">{lc({ pt: "SOLUÇÕES & CATÁLOGO", en: "SOLUTIONS & CATALOG" })}</div>
          <p className="mt-2 text-sm text-slate-500">{lc({ pt: "Do produto do dia a dia a equipamentos e projetos completos.", en: "From everyday products to equipment and complete projects." })}</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Link to="/solutions" data-testid="home-explore-catalog" className="group flex min-h-14 flex-1 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-5 text-sm text-slate-500 transition-colors duration-300 hover:border-bsgo/40 hover:bg-white">
            <Search size={19} className="text-bsgo" />
            <span className="flex-1">{lc({ pt: "Pesquisar produtos, equipamentos ou soluções…", en: "Search products, equipment or solutions…" })}</span>
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy text-white transition-colors duration-300 group-hover:bg-bsgo"><ArrowRight size={17} /></span>
          </Link>
          <Link to="/nexis-quiz" className="inline-flex min-h-14 items-center justify-center gap-2 rounded-xl border border-bsgo/15 px-5 text-sm font-semibold text-bsgo hover:bg-bsgo/5">
            <Sparkles size={18} />{lc({ pt: "Não encontrou?", en: "Can't find it?" })}
          </Link>
        </div>
      </div>
    </section>
  );
}

const HX_AREAS = [
  { icon: Zap, pt: "Energia & Água", en: "Energy & Water" },
  { icon: Building2, pt: "Construção & Espaços", en: "Construction & Spaces" },
  { icon: Cpu, pt: "Mobilidade & Tecnologia", en: "Mobility & Technology" },
  { icon: HeartPulse, pt: "Agricultura & Cuidados", en: "Agriculture & Care" },
  { icon: Dumbbell, pt: "Fitness & Entretenimento", en: "Fitness & Entertainment" },
  { icon: Factory, pt: "Máquinas & Produção", en: "Machinery & Production" },
];

function SolutionsPortfolio() {
  const { lc } = useI18n();
  const tradeCategories = [
    { image: "trade_women", pt: "Mulher", en: "Women" },
    { image: "trade_men", pt: "Homem", en: "Men" },
    { image: "trade_children", pt: "Crianças", en: "Children" },
    { image: "trade_resale", pt: "Para Revenda", en: "For Resale" },
  ];
  return (
    <section className="bg-[#f5f7fa] py-8 lg:py-12" data-testid="home-capabilities">
      <div className="mx-auto grid max-w-[1500px] gap-5 px-6 sm:px-8 lg:grid-cols-[1.06fr_.94fr] lg:px-12">
        <Reveal>
          <Link to="/solutions" data-testid="cap-card-hx-ecosystems" className="image-card group relative block min-h-[520px] overflow-hidden rounded-2xl bg-navy text-white shadow-[0_24px_70px_rgba(7,21,38,.14)]">
            <SmartImage src={img("hx_ecosystems_tropical")} alt={lc({ pt: "Energia solar, construção modular e infraestrutura integrada em ambiente tropical", en: "Solar energy, modular construction and integrated infrastructure in a tropical environment" })} className="image-card-media absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/95 via-navy/48 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
              <div className="premium-overline text-[#f4c15d]">{lc({ pt: "SOLUÇÕES PROFISSIONAIS", en: "PROFESSIONAL SOLUTIONS" })}</div>
              <div className="mt-3 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-[#55d77a]"><Zap size={24} /></span>
                <h2 className="font-display text-3xl font-extrabold sm:text-4xl">HX <span className="font-light">ECOSYSTEMS</span></h2>
              </div>
              <p className="mt-3 max-w-xl text-sm text-white/72 sm:text-base">{lc({ pt: "Equipamentos, sistemas, infraestrutura e projetos integrados.", en: "Equipment, systems, infrastructure and integrated projects." })}</p>
              <div className="mt-7 grid grid-cols-2 gap-2 border-t border-white/15 pt-5 sm:grid-cols-3 lg:grid-cols-6">
                {HX_AREAS.map(({ icon: Icon, pt, en }) => <span key={en} className="flex items-center gap-2 text-[11px] text-white/78"><Icon size={15} className="text-[#55d77a]" />{lc({ pt, en })}</span>)}
              </div>
              <span className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-white">{lc({ pt: "Explorar HX Ecosystems", en: "Explore HX Ecosystems" })}<ArrowRight size={16} /></span>
            </div>
          </Link>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="image-card group relative min-h-[520px] overflow-hidden rounded-2xl bg-[#102b43] text-white shadow-[0_24px_70px_rgba(7,21,38,.14)]" data-testid="cap-card-bsgotech-trade">
            <SmartImage src={img("bsgotech_trade_family")} alt={lc({ pt: "Família num ambiente de compras, produtos e comércio", en: "Family in a shopping, products and trade environment" })} className="image-card-media absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#071526]/95 via-[#071526]/48 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
              <div className="premium-overline text-[#f4c15d]">{lc({ pt: "PRODUTOS & COMÉRCIO", en: "PRODUCTS & TRADE" })}</div>
              <div className="mt-3 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#f4c15d] text-navy"><ShoppingBag size={23} /></span>
                <h2 className="font-display text-3xl font-extrabold sm:text-4xl">BSGOTECH <span className="text-[#f4c15d]">TRADE</span></h2>
              </div>
              <p className="mt-3 max-w-xl text-sm text-white/72 sm:text-base">{lc({ pt: "Produtos, oportunidades comerciais e soluções para revenda, sempre sujeitos a disponibilidade e validação.", en: "Products, commercial opportunities and resale solutions, always subject to availability and validation." })}</p>
              <div className="mt-7 flex flex-wrap gap-2 border-t border-white/15 pt-5 text-[11px] text-white/72">
                {[lc({ pt: "Produtos", en: "Products" }), lc({ pt: "Comércio", en: "Trade" }), lc({ pt: "Revenda", en: "Resale" }), lc({ pt: "Sob consulta", en: "On request" })].map((x) => <span key={x} className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5">{x}</span>)}
              </div>
              <div className="mt-5 grid grid-cols-4 gap-1.5">
                {tradeCategories.map((category) => <div key={category.image} className="relative aspect-[4/3] overflow-hidden rounded-lg border border-white/10"><SmartImage src={img(category.image)} alt={lc({ pt: category.pt, en: category.en })} className="image-card-media h-full w-full object-cover" sizes="(max-width: 768px) 25vw, 160px" /><div className="absolute inset-0 bg-gradient-to-t from-navy/75 via-transparent to-transparent" /><span className="absolute inset-x-1 bottom-1.5 text-center text-[8px] font-bold uppercase tracking-wide text-white">{lc({ pt: category.pt, en: category.en })}</span></div>)}
              </div>
              <div className="mt-7 flex flex-wrap gap-4">
                <Link to="/solutions" className="inline-flex items-center gap-2 text-sm font-semibold text-white">{lc({ pt: "Explorar produtos", en: "Explore products" })}<ArrowRight size={16} /></Link>
                <Link to="/nexis-quiz?cat=product" className="inline-flex items-center gap-2 text-sm font-semibold text-[#f4c15d]">{lc({ pt: "Pedir sourcing", en: "Request sourcing" })}<ArrowRight size={16} /></Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { lc } = useI18n();
  const icons = [Sparkles, Search, PackageSearch, ClipboardCheck, Check, Truck];
  return (
    <section id="how" className="bg-white py-16 lg:py-20" data-testid="home-how">
      <div className="mx-auto max-w-[1500px] px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <div className="premium-overline text-bsgo">{lc({ pt: "COMO FUNCIONA", en: "HOW IT WORKS" })}</div>
            <h2 className="mt-3 font-display text-3xl font-black text-navy sm:text-4xl">{lc({ pt: "Da necessidade à execução.", en: "From need to execution." })}</h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-slate-500">{lc({ pt: "Um processo claro, com aprovação antes de cada compromisso de execução.", en: "A clear process, with approval before any execution commitment." })}</p>
        </div>
        <div className="relative mt-10 grid gap-4 md:grid-cols-3 xl:grid-cols-6">
          <div className="absolute left-[8%] right-[8%] top-7 hidden h-px bg-slate-200 xl:block" />
          {HOW_IT_WORKS.map((step, index) => {
            const Icon = icons[index];
            return (
              <Reveal key={step.n} delay={index * 0.05} className="relative">
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-5 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-bsgo/25 hover:shadow-lg">
                  <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full border-4 border-white bg-navy text-white shadow-sm"><Icon size={21} /></div>
                  <div className="mt-5 text-[10px] font-bold tracking-[.18em] text-[#c48b24]">0{step.n}</div>
                  <h3 className="mt-1 text-sm font-bold text-navy">{lc(step.title)}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-slate-500">{lc(step.text)}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Sourcing() {
  const { lc } = useI18n();
  const steps = [
    { icon: Factory, pt: "Fabricantes e fornecedores", en: "Manufacturers and suppliers" },
    { icon: ShieldCheck, pt: "Verificação e qualidade", en: "Verification and quality" },
    { icon: Ship, pt: "Logística internacional", en: "International logistics" },
    { icon: MapPin, pt: "Coordenação no destino", en: "Destination coordination" },
  ];
  return (
    <section className="relative isolate overflow-hidden bg-navy text-white" data-testid="home-sourcing-network">
      <SmartImage src={img("world_map_light")} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-screen" sizes="100vw" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_65%_45%,rgba(37,99,235,.32),transparent_34%),linear-gradient(90deg,#071526_15%,rgba(7,21,38,.9)_54%,rgba(7,21,38,.68))]" />
      <div className="relative mx-auto grid max-w-[1500px] gap-10 px-6 py-16 sm:px-8 lg:grid-cols-[.85fr_1.15fr] lg:px-12 lg:py-20">
        <Reveal>
          <div className="max-w-xl">
            <div className="premium-overline text-[#f4c15d]">GLOBAL SOURCING</div>
            <h2 className="mt-4 font-display text-4xl font-black leading-tight sm:text-5xl">{lc({ pt: "Da origem ao destino.", en: "From origin to destination." })}</h2>
            <p className="mt-5 text-base leading-relaxed text-white/70">{lc({ pt: "Conectamos fabricantes, fornecedores e parceiros de todo o mundo com necessidades em São Tomé e Príncipe e mercados internacionais.", en: "We connect manufacturers, suppliers and partners worldwide with needs in São Tomé and Príncipe and international markets." })}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/sourcing" data-testid="home-sourcing-cta" className="premium-button premium-button-gold">{lc({ pt: "Saber mais", en: "Learn more" })}<ArrowRight size={17} /></Link>
              <Link to="/sourcing#request" className="premium-button premium-button-ghost">{lc({ pt: "Solicitar cotação", en: "Request a quote" })}</Link>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="grid gap-3 sm:grid-cols-2">
            {steps.map(({ icon: Icon, pt, en }) => (
              <div key={en} className="flex items-center gap-4 rounded-xl border border-white/12 bg-white/[.06] p-4 backdrop-blur-sm">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white/10 text-[#f4c15d]"><Icon size={21} /></span>
                <span className="text-sm font-medium text-white/85">{lc({ pt, en })}</span>
              </div>
            ))}
            <div className="sm:col-span-2 mt-2 flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold tracking-wide text-white/55">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[.05] px-3 py-2 text-white/75">CHINA <ArrowRight size={13} /> SÃO TOMÉ E PRÍNCIPE</span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[.05] px-3 py-2 text-white/75">CHINA <ArrowRight size={13} /> CABO VERDE</span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const STRATEGIC_AREAS = [
  { key: "experts", to: "/experts", image: "energy_install", icon: GraduationCap, title: { pt: "Especialistas & Formação", en: "Experts & Training" }, text: { pt: "Engenheiros, técnicos e formadores para apoiar projetos reais.", en: "Engineers, technicians and trainers supporting real projects." } },
  { key: "eco", to: "/eco-collection", image: "eco_collection", icon: MapPin, title: { pt: "Eco Collection", en: "Eco Collection" }, text: { pt: "Turismo autêntico em São Tomé, Príncipe e Cabo Verde.", en: "Authentic tourism in São Tomé, Príncipe and Cabo Verde." } },
  { key: "network", to: "/network", image: "partner_network", icon: Users, title: { pt: "Network", en: "Network" }, text: { pt: "Uma necessidade. A capacidade certa.", en: "One need. The right capability." } },
  { key: "projects", to: "/projects", image: "mercado_modular", icon: Map, title: { pt: "Projetos", en: "Projects" }, text: { pt: "Conceitos, pilotos e projetos com estado transparente.", en: "Concepts, pilots and projects with transparent status." } },
];

function StrategicAreas() {
  const { lc } = useI18n();
  return (
    <section className="bg-[#f5f7fa] py-14 lg:py-20" data-testid="home-strategic-areas">
      <div className="mx-auto max-w-[1500px] px-6 sm:px-8 lg:px-12">
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {STRATEGIC_AREAS.map((area, index) => {
            const Icon = area.icon;
            return (
              <Reveal key={area.key} delay={index * 0.06}>
                <Link to={area.to} className="image-card group block h-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`strategic-${area.key}`}>
                  <div className="relative aspect-[16/10] overflow-hidden bg-slate-200">
                    <SmartImage src={img(area.image)} alt={lc(area.title)} className="image-card-media h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/45 to-transparent" />
                    <span className="absolute bottom-4 left-4 flex h-10 w-10 items-center justify-center rounded-lg bg-white/92 text-bsgo shadow"><Icon size={20} /></span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-xl font-extrabold text-navy">{lc(area.title)}</h3>
                    <p className="mt-2 min-h-10 text-sm leading-relaxed text-slate-500">{lc(area.text)}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-bsgo">{lc({ pt: "Saber mais", en: "Learn more" })}<ArrowRight size={14} /></span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function NexisLayer() {
  const { lc } = useI18n();
  const capabilities = [
    { icon: Sparkles, to: "/nexis-quiz", name: "NEXIS Assist", pt: "Compreensão e encaminhamento de necessidades", en: "Need understanding and routing" },
  ];
  return (
    <section className="border-y border-cyan-300/10 bg-[#031c2c] text-white" data-testid="home-nexis-layer">
      <div className="mx-auto grid max-w-[1500px] gap-8 px-6 py-8 sm:px-8 lg:grid-cols-[280px_1fr_190px] lg:items-center lg:px-12">
        <div className="flex items-center gap-4">
          <span className="font-display text-5xl font-black tracking-[-.08em] text-cyan-400">N</span>
          <div>
            <div className="text-xs uppercase tracking-[.25em] text-white/50">Powered by</div>
            <div className="font-display text-2xl font-extrabold">NEXIS</div>
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-[.22em] text-cyan-300">Digital · AI · Automation</div>
          </div>
        </div>
        <div>
          <div className="grid grid-cols-1 gap-2">
            {capabilities.map(({ icon: Icon, to, name, pt, en }) => (
              <Link key={name} to={to} className="group rounded-xl border border-white/10 bg-white/[.035] p-4 transition-colors duration-300 hover:border-cyan-300/35 hover:bg-white/[.07]">
                <Icon size={20} className="text-cyan-300" />
                <div className="mt-3 text-sm font-bold">{name}</div>
                <div className="mt-1 text-[11px] text-white/50">{lc({ pt, en })}</div>
              </Link>
            ))}
          </div>
          <Link to="/bs-call-pro" data-testid="home-bs-call-pro" className="group mt-2 flex items-center gap-4 rounded-xl border border-cyan-300/15 bg-cyan-300/[.035] p-2.5 transition-colors duration-300 hover:border-cyan-300/35 hover:bg-cyan-300/[.07]">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-cyan-300/15 bg-[radial-gradient(circle_at_35%_25%,rgba(34,211,238,.2),transparent_55%),#06182a] text-cyan-300"><Languages size={22} /></span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1"><strong className="text-sm text-white">BS CALL PRO</strong><span className="text-[9px] font-bold uppercase tracking-[.14em] text-cyan-300">Concept / In Development</span></div>
              <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-[.17em] text-white/40">Powered by NEXIS</div>
              <div className="mt-0.5 text-[9px] font-semibold uppercase tracking-[.14em] text-white/30">Developed by BSGOTECH</div>
              <p className="mt-1 truncate text-[11px] text-white/50">Speak your language. Understand everyone.</p>
            </div>
            <ArrowRight size={15} className="shrink-0 text-cyan-300 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link to="/nexis-builder" data-testid="home-nexis-builder" className="group mt-2 flex items-center gap-4 rounded-xl border border-cyan-300/15 bg-cyan-300/[.035] p-2.5 transition-colors duration-300 hover:border-cyan-300/35 hover:bg-cyan-300/[.07]">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-cyan-300/15 bg-[radial-gradient(circle_at_35%_25%,rgba(34,211,238,.2),transparent_55%),#06182a] text-cyan-300"><Braces size={22} /></span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1"><strong className="text-sm text-white">NEXIS BUILDER</strong><span className="text-[9px] font-bold uppercase tracking-[.14em] text-cyan-300">Concept / In Development</span></div>
              <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-[.17em] text-white/40">Powered by NEXIS</div>
              <p className="mt-1 truncate text-[11px] text-white/50">Describe. Build. Review. Ship.</p>
            </div>
            <ArrowRight size={15} className="shrink-0 text-cyan-300 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="border-t border-white/10 pt-5 lg:border-l lg:border-t-0 lg:pl-7 lg:pt-0">
          <div className="font-display text-lg font-extrabold uppercase leading-tight">NEXIS assists.<br />Human approves.</div>
          <p className="mt-2 text-[11px] leading-relaxed text-white/50">{lc({ pt: "Mais eficiência. Mais controlo. Mais resultados.", en: "More efficiency. More control. More results." })}</p>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <SeoHead
        title="BSGOTECH | Da necessidade à solução"
        description="Soluções, equipamentos, sourcing global, especialistas e projetos tecnológicos para São Tomé e Príncipe, Cabo Verde e mercados internacionais."
        image={img("stp_hero_coast")}
      />
      <Hero />
      <SolutionSearch />
      <SolutionsPortfolio />
      <HowItWorks />
      <Sourcing />
      <StrategicAreas />
      <NexisLayer />
    </div>
  );
}
